import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Observable, of, Subject } from 'rxjs';
import { catchError, distinctUntilChanged, finalize, map, startWith, switchMap, takeUntil, tap } from 'rxjs/operators';
import { TemplateCategoriesService, TemplateCategory, TemplateCategoryFormData } from '../template-categories.service';
import { Generic } from 'src/app/interfaces/generic.interface';
import { CommonService } from 'src/app/services/common.service';
import { helper } from 'src/environments/helper';
import { NotificationService } from 'src/app/services/notification.service';
import { environment } from 'src/environments/environment';

@Component({
    selector: 'app-edit-template-category',
    templateUrl: './edit-template-category.component.html',
    styleUrls: ['./edit-template-category.component.scss']
})
export class EditTemplateCategoryComponent implements OnInit, OnDestroy {
    // general error
    errorTitle: string = environment.config.customNotifications.headers.error;
    errorIcon: string = environment.config.customNotifications.icons.error;
    errorType: string = environment.config.customNotifications.icons.error;

    isLoading = false;
    helper = helper;
    // Parent category selection
    availableParentCategories: TemplateCategory[] = [];

    directiiActive: Generic[] = this.data.directiiActive;
    compartimenteActive!: Generic[];

    private editDestroy$ = new Subject<void>();

    filteredDirectii: any[] = this.data.directiiActive;
    directiiControl = new FormControl();

    filteredCompartimente: any[] = [];
    compartimenteControl = new FormControl();

    filteredParentCategories: any[] = [];
    parentCategoriesControl = new FormControl();

    form: FormGroup = new FormGroup({
        name: new FormControl(this.data.data.name, [Validators.required]),
        description: new FormControl(this.data.data.description),
        directie_id: new FormControl(this.data.data.directie_id, [Validators.required]),
        compartiment_id: new FormControl(this.data.data.compartiment_id ? this.data.data.compartiment_id : -1, [Validators.required]),
        parent_category_id: new FormControl(this.data.data.parent_category_id ? this.data.data.parent_category_id : -1, []),
        order_index: new FormControl(this.data.data.order_index || 0, []),
    });

    constructor(
        private fb: FormBuilder,
        private dialogRef: MatDialogRef<EditTemplateCategoryComponent>,
        @Inject(MAT_DIALOG_DATA) public data: any,
        private templateCategoriesService: TemplateCategoriesService,
        private commonService: CommonService,
        private notificationService: NotificationService
    ) {
        console.log(this.data);
    }

    ngOnInit(): void {
        if (this.data.data.directie_id) {
            this.loadCompartimenteActive(this.data.data.directie_id);
        }

        this.renderFieldsSearch();
        this.loadAvailableParentCategories();
    }

    ngOnDestroy(): void {
        this.editDestroy$.next();
        this.editDestroy$.complete();
    }

    closeDialog() {
        this.dialogRef?.close({
            success: false,
        });
    }

    renderFieldsSearch() {
        this.directiiControl.valueChanges.pipe(
            takeUntil(this.editDestroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredDirectii = this.directiiActive;
                    return of(this.directiiActive);
                }
                return this.filterDirectii(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredDirectii = data;
            }),
            catchError(() => of([]))
        ).subscribe();

        this.compartimenteControl.valueChanges.pipe(
            takeUntil(this.editDestroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredCompartimente = this.compartimenteActive;
                    return of(this.compartimenteActive);
                }
                return this.filterCompartimente(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredCompartimente = data;
            }),
            catchError(() => of([]))
        ).subscribe();

        this.parentCategoriesControl.valueChanges.pipe(
            takeUntil(this.editDestroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredParentCategories = this.availableParentCategories;
                    return of(this.availableParentCategories);
                }
                return this.filterParentCategories(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredParentCategories = data;
            }),
            catchError(() => of([]))
        ).subscribe();
    }

    normalizeString(value: string): string {
        return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    }

    filterDirectii(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        const filtered = this.directiiActive.filter(directie =>
            this.normalizeString(directie.name).includes(normalizedSearchTerm)
        );

        return of(filtered);
    }

    onDirectieSelectClosed() {
        const selectedValue = this.directiiControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredDirectii.some(directie =>
                directie.id === this.form.get('directie_id')?.value
            );

            if (!isSelectedInFilteredList) {
                this.directiiControl.setValue(null);
                this.filteredDirectii = this.directiiActive;
                this.editDestroy$.next();
                this.editDestroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    onDirectieChange(directieId: number): void {
        this.form.controls['compartiment_id'].patchValue(-1);
        this.form.controls['parent_category_id'].patchValue(-1);

        if (directieId == -1) {
            this.compartimenteActive = [];
            this.filteredCompartimente = [];
            this.availableParentCategories = [];
            this.filteredParentCategories = [];
            return;
        }

        if (directieId) {
            this.loadCompartimenteActive(directieId);
            this.loadAvailableParentCategories();
        }
    }

    loadCompartimenteActive(directieId?: number) {
        this.isLoading = true;
        let compartimenteObservable$ = directieId
            ? this.commonService.getCompartimenteByDirectieId(directieId)
            : this.commonService.getCompartimenteActive();

        compartimenteObservable$.pipe(
            map((results: any) => [...results]),
            finalize(() => {
                this.isLoading = false;
            })
        ).subscribe({
            next: (compartimente) => {
                this.compartimenteActive = [...compartimente];
                this.filteredCompartimente = [...compartimente];
            },
            error: (err) => {
                console.log(err);
                this.filteredCompartimente = [];
            },
        })
    }

    filterCompartimente(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        const filtered = this.compartimenteActive.filter(compartiment =>
            this.normalizeString(compartiment.name).includes(normalizedSearchTerm)
        );

        return of(filtered);
    }

    onCompartimentSelectClosed() {
        const selectedValue = this.compartimenteControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredCompartimente.some(compartiment =>
                compartiment.id === this.form.get('compartiment_id')?.value
            );

            if (!isSelectedInFilteredList) {
                this.compartimenteControl.setValue(null);
                this.filteredCompartimente = this.compartimenteActive;
                this.editDestroy$.next();
                this.editDestroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    onCompartimentChange(compartimentId: number) {
        this.form.controls['parent_category_id'].patchValue(-1);

        if (compartimentId) {
            this.loadAvailableParentCategories();
        }
    }

    loadAvailableParentCategories(): void {
        const directieId = this.form.get('directie_id')?.value;
        const compartimentId = this.form.get('compartiment_id')?.value;

        if (!directieId) {
            this.availableParentCategories = [];
            return;
        }

        this.templateCategoriesService.getCategoriesByOrganization(directieId, compartimentId)
            .subscribe({
                next: (response) => {
                    if (response.errors === false) {
                        // Filter out current category and its descendants to prevent circular references
                        this.availableParentCategories = (response.data || []).filter((cat: TemplateCategory) =>
                            cat.id !== this.data.data.id && !this.isDescendant(cat, this.data.data.id)
                        );
                        this.filteredParentCategories = [...this.availableParentCategories];
                    }
                },
                error: (error) => {
                    console.error('Error loading parent categories:', error);
                    this.availableParentCategories = [];
                }
            });
    }

    filterParentCategories(searchTerm: string): Observable<Generic[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        return of(this.availableParentCategories.filter(category =>
            this.normalizeString(category.name).includes(normalizedSearchTerm)
        ));
    }

    onParentCategoriesSelectClosed() {
        const selectedValue = this.parentCategoriesControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredParentCategories.some(category =>
                category.id === this.form.get('parent_category_id')?.value
            );

            if (!isSelectedInFilteredList) {
                this.parentCategoriesControl.setValue(null);
                this.filteredParentCategories = this.availableParentCategories;
                this.editDestroy$.next();
                this.editDestroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    isDescendant(category: TemplateCategory, parentId: number): boolean {
        // Simple check to prevent circular references
        // In a real implementation, you'd need a more robust check
        if (category.parent_category_id === parentId) {
            return true;
        }
        // This would need to be recursive for deep hierarchies
        return false;
    }

    submitForm() {
        this.isLoading = true;

        const {
            name,
            description,
            directie_id,
            compartiment_id,
            parent_category_id,
            order_index
        } = this.form.value as TemplateCategoryFormData;

        const updateRequestType: TemplateCategoryFormData = {
            name,
            description,
            directie_id,
            compartiment_id,
            parent_category_id,
            order_index
        };

        this.templateCategoriesService
            .update(this.data.data.id, updateRequestType)
            .pipe(finalize(() => (this.isLoading = false)))
            .subscribe({
                next: () => {
                    this.dialogRef.close({ success: true });
                },
                error: (err) => {
                    let msg
                    try {
                        // try to catch the message from server
                        msg = err.error.errors.message[0]
                    } catch (error) {
                        // controll output message
                        msg = helper.dialogConfig.generalMessages.error
                    }
                    this.notificationService.warningSwal(
                        helper.dialogConfig.headers.errorAtentie,
                        msg,
                        this.errorIcon
                    );
                },
            });
    }

    onHttpError(res: any) {
        if (res.errors && res.errors.message && res.errors.message.length > 0) {
            res.errors.message.forEach((msg: string) => {
                this.notificationService.warningSwal(
                    this.errorTitle,
                    msg,
                    this.errorIcon
                );
            });
        } else {
            let errorMessage =
                environment.config.customNotifications.generalMessages.error;
            this.notificationService.warningSwal(
                this.errorTitle,
                errorMessage,
                this.errorIcon
            );
        }
    }
}